import styled from 'styled-components';
import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {connect} from 'react-redux';
import {
    Material,
    MaterialEdge,
    MaterialType,
} from 'components/customer/Materials/entity';
import {AppState} from 'store/customer/storeSetup';
import {getMaterial} from 'components/customer/Materials/store/selectors/materialSelector';
import {getEdge} from 'components/customer/Materials/store/selectors/edgeSelector';
import {isMobile} from 'react-device-detect';

interface ColorSwatchSelectedType {
    horizontal: boolean;
    isEdge?: boolean;
    containerClass?: string;
    selectedColor?: Material;
    selectedEdge?: MaterialEdge;
    isExterior?: boolean;
    size?: number;
    isSmallThumbnail?: boolean;
}

interface ThumbnailInterface {
    $size?: number;
    $isHorizontal?: boolean;
    $isSmallThumbnail?: boolean;
    $colorImage?: string;
    $edgeImage?: string;
    $isMobileDevice?: boolean;
}

const thumbnailSize = 130;
const mediumThumbnailSize = 110;
const smallThumbnailSize = 70;

export const ColorSwatchSelectedComponent = ({
    horizontal = false,
    isEdge = false,
    containerClass = '.col',
    selectedColor,
    selectedEdge,
    size,
    isSmallThumbnail = false,
}: ColorSwatchSelectedType): JSX.Element => {
    const colorThumbnailRef = useRef<HTMLDivElement>();
    const isSmallDevice = isDeviceSmall();

    const [smallThumbnail, setSmallThumbnail] = useState(false);

    useLayoutEffect(() => {
        // do not alter state if showSmallThumbnail is true
        if (colorThumbnailRef.current && typeof size === 'undefined') {
            let isThumbnailSmall = smallThumbnail;
            const container = colorThumbnailRef.current.closest(containerClass);

            if (container) {
                const containerDimension = container.getBoundingClientRect();
                const thumbnailDimension =
                    colorThumbnailRef.current.getBoundingClientRect();

                if (thumbnailDimension.width >= containerDimension.width - 40) {
                    isThumbnailSmall = true;
                }
            }

            if (isThumbnailSmall != smallThumbnail) {
                setSmallThumbnail(isThumbnailSmall);
            }
        }
    }, []);

    const thumbSize = useMemo(() => {
        if (typeof size !== 'undefined') {
            return size;
        } else {
            if (isSmallDevice || smallThumbnail) {
                if (isSmallDevice) {
                    return mediumThumbnailSize;
                } else {
                    return smallThumbnailSize;
                }
            } else {
                return thumbnailSize;
            }
        }
    }, [size, smallThumbnail, isSmallDevice]);

    if (isEdge) {
        return (
            <ThumbnailEdgeContainer
                ref={colorThumbnailRef}
                $size={thumbSize}
                $isSmallThumbnail={isSmallDevice || smallThumbnail}
                $edgeImage={selectedEdge ? selectedEdge.image : undefined}
                $isHorizontal={horizontal}>
                <ThumbnailColourEdgeRight
                    $isSmallThumbnail={isSmallThumbnail}
                />
            </ThumbnailEdgeContainer>
        );
    }

    return (
        <ThumbnailContainer
            className="thumbnail-container"
            ref={colorThumbnailRef}
            $size={thumbSize}
            $isSmallThumbnail={isSmallThumbnail}
            $isMobileDevice={isMobile}
            $colorImage={selectedColor ? selectedColor.image : undefined}
            $edgeImage={selectedEdge ? selectedEdge.image : undefined}
            $isHorizontal={horizontal}>
            <ThumbnailColourEdgeLeft $isSmallThumbnail={isSmallThumbnail} />
            <ThumbnailColour />
            <ThumbnailColourEdgeRight $isSmallThumbnail={isSmallThumbnail} />
        </ThumbnailContainer>
    );
};

export const ColorSwatchSelected = connect(
    (state: AppState, props: ColorSwatchSelectedType) => ({
        selectedColor: getMaterial(
            state,
            props.isExterior ? MaterialType.EXTERIOR : MaterialType.CARCASE
        ),
        selectedEdge: getEdge(
            state,
            props.isExterior ? MaterialType.EXTERIOR : MaterialType.CARCASE
        ),
    })
)(ColorSwatchSelectedComponent);

export const ThumbnailColour = styled.div`
    border: 2px solid black;
    box-sizing: border-box;
    padding: 0;
    background-repeat: no-repeat;
    background-size: cover;
`;

export const ThumbnailColourEdgeLeft = styled.div<{
    $isSmallThumbnail?: boolean;
}>`
    width: ${({$isSmallThumbnail}) => ($isSmallThumbnail ? 5 : 10)}px;
    border: 2px solid black;
    border-right: 0;
    top: ${({$isSmallThumbnail}) => ($isSmallThumbnail ? 3 : 5)}px;
    left: ${({$isSmallThumbnail}) => ($isSmallThumbnail ? -5 : -10)}px;
    bottom: ${({$isSmallThumbnail}) => ($isSmallThumbnail ? -3 : -6)}px;
    position: absolute;
    transform: skewY(-45deg);
    border-bottom: 0;
    background-repeat: no-repeat;
    background-size: cover;
`;

export const ThumbnailColourEdgeRight = styled.div<{
    $isSmallThumbnail?: boolean;
}>`
    height: ${({$isSmallThumbnail}) => ($isSmallThumbnail ? 5 : 10)}px;
    border: 2px solid black;
    border-top: 0;
    left: ${({$isSmallThumbnail}) => ($isSmallThumbnail ? -3 : -5)}px;
    bottom: ${({$isSmallThumbnail}) => ($isSmallThumbnail ? -5 : -10)}px;
    right: ${({$isSmallThumbnail}) => ($isSmallThumbnail ? 3 : 5)}px;
    position: absolute;
    transform: skewX(-45deg);
    background-repeat: no-repeat;
    background-size: cover;
`;

export const ThumbnailContainer = styled.div<ThumbnailInterface>`
    margin-top: 10px;
    margin-left: 15px;
    position: relative;
    width: ${(props) => props.$size}px;
    height: ${(props) => props.$size}px;
    cursor: pointer;

    ${ThumbnailColour} {
        width: 100%;
        height: 100%;
        background: ${(props) => `url("${props.$colorImage}")`};

        ${(props) => {
            if (props.$isHorizontal) {
                return `transform: rotate(90deg)`;
            }
        }}
    }

    ${ThumbnailColourEdgeLeft} {
        position: absolute;
        background: ${(props) => `url("${props.$edgeImage}")`};

        ${({$isHorizontal, $isSmallThumbnail}) => {
            if ($isHorizontal) {
                return `
                transform-origin: top left;
                transform: rotate(90deg) skewX(45deg);
                top: 0;
                left: 0;
                width: 100%;
                height: ${$isSmallThumbnail ? 5 : 10}px;
                border-top: 0;
                border-right: 2px solid black;
                border-bottom: 2px solid black;`;
            }
        }}
    }

    ${ThumbnailColourEdgeRight} {
        position: absolute;
        background: ${(props) => `url("${props.$edgeImage}")`};

        ${({$isHorizontal, $isSmallThumbnail}) => {
            if ($isHorizontal) {
                return `
                transform-origin: bottom left;
                transform: rotate(90deg) skewY(45deg);
                top: 0;
                left: 0%;
                height: 100%;
                width: ${$isSmallThumbnail ? 5 : 10}px;
                border-top: 2px solid black;
                border-bottom: 2px solid black;
                border-left: 0;`;
            }
        }}
    }
`;

const ThumbnailEdgeContainer = styled(ThumbnailContainer)`
    height: 100%;
    width: ${(props) => props.$size}px;
    margin-left: 0;
    margin-top: 0;
    position: initial;

    ${ThumbnailColourEdgeRight} {
        position: relative;
        border: 2px solid black;
        height: 10px;
        width: ${(props) => props.$size}px;
        transform: skew(-45deg);
        top: initial;
        bottom: initial;
        right: initial;
        left: initial;
        margin-top: 50px;
        margin-left: 10px;
    }
`;
